import Phaser from 'phaser';
import { EnemyType, EnemyGrade, ENEMY_STATS, GRADE_MODIFIERS, TILE_SIZE, FLYING_ENEMY_TYPES, KNOCKBACK_IMMUNE_MASS, AGGRO_ENEMY_TYPES, ENEMY_TEXTURES, ENEMY_SPRITE_FILES, TileType, getResistanceMultiplier, EnemyWeaponDef, DIFFICULTY_SPEED_MULT, DECOR_INTERACTIONS, DecorInteraction } from '@/config/GameConfig';
import { ProjectileType } from '@/components/ComponentDefs';
import { EventBus, GameEvents } from '@/events/EventBus';
import { GOD, getEnemyTypeScale, getEnemyTypeStatOverride } from '@/config/GodMode';
import { Robot, popupFontSize } from '@/entities/Robot';
import { BilliardKnockback } from '@/systems/BilliardKnockback';
import { VFXPool } from '@/systems/ObjectPool';
import { SoundManager } from '@/systems/SoundManager';
import { MAP_GRID, getDecorAt } from '@/map/MapData';
import { hasLineOfSight } from '@/systems/LineOfSight';

// ── Extracted modules ──
import {
  updateGround as _updateGround,
  updateFlyer as _updateFlyer,
  generateFlightPath,
  resyncToNearestWaypoint as _resyncToNearestWaypoint,
  recalcPath as _recalcPath,
  clampToValidPosition as _clampToValidPosition,
} from '@/entities/enemy/EnemyMovement';
import {
  updateCloak as _updateCloak,
  updateBurn as _updateBurn,
  spawnBurnParticle as _spawnBurnParticle,
  spawnHitSpark as _spawnHitSpark,
  spawnComboPopup as _spawnComboPopup,
  showAlertPopup as _showAlertPopup,
  showConfusionPopup as _showConfusionPopup,
  updateSpriteStack as _updateSpriteStack,
  startTerrainDeath as _startTerrainDeath,
  updateTerrainDying as _updateTerrainDying,
  updateHpBar as _updateHpBar,
} from '@/entities/enemy/EnemyVisuals';
import {
  updateShooting as _updateShooting,
  updateAggroTarget as _updateAggroTarget,
  onReachedBase as _onReachedBase,
  updateBaseSiege as _updateBaseSiege,
} from '@/entities/enemy/EnemyCombat';

export interface EnemyConfig {
  hp: number;
  speed: number;
  radius: number;
  color: number;
  reward: number;
  baseDamage: number;
}

/** Robot target data passed to enemy shooting system */
export interface RobotTarget {
  x: number;
  y: number;
  alive: boolean;
  isCamoActive?: boolean;
  getExposedTarget?: () => { x: number; y: number } | null;
}

export class Enemy {
  /** Gap in pixels between sprite edge and HP bar — tweak this single value to adjust */
  static readonly BAR_GAP = 4;
  static readonly SCALE_ENEMY = 8;
  /** Minimum display size in pixels — ensures small enemies (Swarm, Wasp) stay visible */
  static readonly MIN_DISPLAY_SIZE = 50;
  /** Melee attack range (px from edge to edge) */
  static readonly MELEE_RANGE = 20;
  /** Default cooldown between melee lunges (overridden by per-type meleeCooldown) */
  static readonly MELEE_COOLDOWN = 1.0;
  /** Lunge distance in pixels */
  static readonly MELEE_LUNGE_DIST = 8;

  private static nextId: number = 0;
  public readonly id: string;

  public scene: Phaser.Scene;
  public body: Phaser.GameObjects.Sprite;
  public hpBar: Phaser.GameObjects.Rectangle;
  public hpBarBg: Phaser.GameObjects.Rectangle;
  public label: Phaser.GameObjects.Text;

  public enemyType: EnemyType;
  public grade: EnemyGrade;
  public waveScaling: number = 0;
  public maxHp: number;
  public hp: number;
  public speed: number;
  public reward: number;
  public baseDamage: number;
  public alive: boolean = true;
  // Base stats before GOD multipliers (for live GOD updates without compounding)
  private baseMaxHp: number = 0;
  private baseSpeed: number = 0;
  private baseBaseDamage: number = 0;
  /** @internal Per-type range multiplier from EnemyStatOverride (fine-tuning) */
  public typeRangeMult: number = 1;
  // Per-weapon base damages stored in weaponBaseDamages[]

  // Gold grade sparkle particles
  private goldSparkleTimer: number = 0;

  // Hive crawling animation
  /** @internal */
  public crawlPhase?: number;
  /** @internal */
  public baseScaleX?: number;
  /** @internal */
  public baseScaleY?: number;

  // ── Weapon combo tracking ──
  /** @internal */
  public comboTypes = new Set<string>();
  /** @internal */
  public comboTimer: number = 0;
  // Combo window & multipliers now read from GOD (combat tuning)
  private static get COMBO_WINDOW(): number { return GOD.comboWindow; }
  private static get COMBO_MULTS(): number[] { return [1.0, 1.0, GOD.comboMult2, GOD.comboMult3, GOD.comboMult4]; }

  // Stun state
  /** @internal */
  public stunTimer: number = 0;

  // Burn DOT state (flamethrower afterburn)
  /** @internal */
  public burnTimer: number = 0;
  /** @internal */
  public burnDps: number = 0;
  /** @internal */
  public burnTickTimer: number = 0;
  /** @internal */
  public burnParticles: Phaser.GameObjects.GameObject[] = [];
  /** @internal */
  public burnSizzleTimer: number = 0;

  /** @internal */
  public waypoints: { x: number; y: number }[];
  /** @internal */
  public currentWaypointIndex: number = 0;
  /** @internal */
  public reachedBase: boolean = false;

  // A* pathfinding: recalculate when pushed off-path
  /** @internal */
  public pathDirty: boolean = false;
  /** @internal */
  public pathRecalcCooldown: number = 0;

  // Shadows (ground = tight under body, flyer = offset + larger)
  private flyShadow?: Phaser.GameObjects.Ellipse;
  private groundShadow?: Phaser.GameObjects.Ellipse;
  private static readonly FLY_SCALE_BOOST = 1.12;
  private static readonly SHADOW_OFFSET_X = 10;
  private static readonly SHADOW_OFFSET_Y = 16;
  /** Altitude bobbing */
  private flyAltitudePhase = Math.random() * Math.PI * 2;
  private flyBaseScale = 1; // stored at creation for bobbing reference

  // Flyer-specific state
  public isFlyer: boolean = false;
  /** @internal */
  public flyerPath: { x: number; y: number }[] = [];
  /** @internal */
  public flyerWpIndex: number = 0;

  // Cloak state
  public isCloaked: boolean = false;
  /** @internal */
  public cloakTimer: number = 0;
  /** @internal */
  public cloakCooldown: number = 0;
  /** @internal */
  public cloakDuration: number = 0;
  /** @internal */
  public cloakCooldownMax: number = 0;
  /** @internal */
  public cloakAlpha: number = 1;
  /** @internal */
  public cloakOverlay: Phaser.GameObjects.Rectangle | null = null;
  /** @internal */
  public wasCloaked: boolean = false;

  // Stun stars visual
  private stunStars: Phaser.GameObjects.Container | null = null;
  private stunStarsAngle: number = 0;

  // Sprite stacking (ghostly layered effect for Phantom/Specter when moving)
  /** @internal */
  public spriteStackLayers: Phaser.GameObjects.Image[] = [];
  static readonly STACK_LAYER_COUNT = 4;
  static readonly STACK_LAYER_SPACING = 3;
  static readonly STACK_MIN_SPEED = 0.3;
  /** @internal */
  public stackPhase: number = Math.random() * Math.PI * 2;
  /** @internal */
  public prevBodyX: number = 0;
  /** @internal */
  public prevBodyY: number = 0;
  /** @internal */
  public hasSpriteStack: boolean = false;

  // Mass for pushback resistance
  public mass: number = 50;

  // Billiard knockback state
  public kbVx: number = 0;
  public kbVy: number = 0;
  public isKnockedBack: boolean = false;
  public kbBounceCount: number = 0;
  public kbChainDepth: number = 0;
  public kbSimTime: number = 0;
  private kbPrevRotation: number = 0;

  // Separation steering (set by WaveManager each frame before movement)
  public separationX: number = 0;
  public separationY: number = 0;
  // Arc formation steering (set by WaveManager for enemies sharing a firing target)
  public arcSteerX: number = 0;
  public arcSteerY: number = 0;
  // Smoothed steering direction (prevents lateral flickering during weapon engagement)
  public _smoothSteerX: number = 0;
  public _smoothSteerY: number = 0;
  /** Current firing target position (set when enemy is in firing range, null otherwise) */
  public firingTarget: { x: number; y: number } | null = null;
  /** Time (seconds) enemy has had a firingTarget but hasn't actually shot — drives close-in heuristic */
  public notFiringTimer: number = 0;
  /** @internal Cached nearby robot refs for weapon-range engagement check in updateGround */
  public nearbyRobots: Robot[] = [];

  // Visual radius = half the displayed sprite size, used for bar offsets
  /** @internal */
  public visualRadius: number;

  // Aggro: position of the robot this enemy is rushing toward (null = follow path)
  /** @internal */
  public aggroTarget: { x: number; y: number } | null = null;
  /** Tracks how long enemy hasn't made progress toward aggro target (stuck detection) */
  public aggroStuckTimer: number = 0;
  /** Last recorded distance to aggro target (for stuck detection) */
  public aggroLastDist: number = Infinity;
  /** Blocking robot that this enemy must attack before passing */
  public blockingRobot: { x: number; y: number; takeDamage: (d: number, ax?: number, ay?: number) => void; alive: boolean } | null = null;
  public canAggro: boolean = false;
  /** True when enemy is within melee/contact range of its aggro target — stops moving closer */
  public inMeleeContact: boolean = false;
  /** A* detour path when stuck behind a wall during aggro */
  public aggroDetourPath: { x: number; y: number }[] | null = null;
  /** Current waypoint index in the aggro detour path */
  public aggroDetourIndex: number = 0;

  // Collision radius (physics, from stats)
  public collisionRadius: number;

  // Healer: periodic healing pulse timer (driven by WaveManager)
  public healTickTimer: number = 0;
  /** @internal visual: green aura circle displayed during heal pulse */
  public healAura: Phaser.GameObjects.Arc | null = null;


  // Alert popup flag — only show "!" once per aggro session, reset when aggro drops
  /** @internal */
  public alertShownForCurrentAggro: boolean = false;
  /** Cooldown: game time of last alert popup (prevents flicker spam) */
  public lastAlertTime: number = 0;
  /** Grace period: frames remaining before aggro can fully drop */
  public aggroGraceTimer: number = 0;

  // Anti-flicker: timestamp of last direction flip
  /** @internal */
  public lastFlipTime: number = 0;

  // Camo panic: enemy sensed a cloaked robot but couldn't detect it
  /** @internal */
  public camoConfusionTimer: number = 0;
  private static readonly CAMO_CONFUSION_DURATION = 2.0;

  // Melee attack state
  /** @internal */
  public meleeCooldown: number = 0;
  /** @internal */
  public meleeCooldownDuration: number = Enemy.MELEE_COOLDOWN;
  private isLunging: boolean = false;

  // Runner dash: short speed burst toward target
  private dashCooldown: number = 0;
  private dashTimer: number = 0;
  /** @internal */
  public isDashing: boolean = false;
  static readonly DASH_COOLDOWN = 3.0;
  static readonly DASH_DURATION = 0.3;
  static readonly DASH_SPEED_MULT = 3.0;
  static readonly DASH_TRIGGER_RANGE = 120;

  // ── Animation state ──
  public animState: 'idle' | 'walk' | 'attack' | 'death' = 'idle';
  public isDying: boolean = false;
  private hasWalkAnim: boolean = false;
  private hasAttackAnim: boolean = false;
  private hasDeathAnim: boolean = false;
  private animKeyBase: string = ''; // e.g. 'enemy_grunt'

  // ── Terrain death state (drowning / burning / falling) ──
  public terrainDyingType: 'water' | 'magma' | 'chasm' | null = null;
  /** @internal */
  public terrainDyingTimer: number = 0;
  /** @internal */
  public terrainDyingDuration: number = 0;

  // Fog of war visibility factor (0 = hidden, 1 = fully visible)
  public fogVisibility: number = 1;
  public fogVisible: boolean = true;

  /** Preview mode: disables movement, pathfinding, and event emission */
  public isPreview: boolean = false;
  /** Fog-immune: always visible regardless of fog-of-war (used by cinematic demo enemies) */
  public fogImmune: boolean = false;
  /** Extra scale multiplier for preview display (applied on top of GOD scale) */
  public previewScale: number = 1;

  /** True when enemy has reached attack range of the player base and is sieging */
  public attackingBase = false;
  /** @internal Timer for periodic melee damage to player base (melee-only enemies) */
  public meleeBaseTimer = 0;

  // Multi-weapon system: runtime state per weapon slot
  public weaponDefs: EnemyWeaponDef[] = [];
  /** @internal */
  public weaponTimers: number[] = [];
  private weaponBaseDamages: number[] = [];
  /** @internal */
  public weaponDamages: number[] = [];
  // Burst state per weapon (only for BURST type)
  /** @internal */
  public burstRemaining: number[] = [];
  /** @internal */
  public burstDelayTimer: number[] = [];
  // Global weapon lock: only one weapon (including CQC) can fire at a time
  /** @internal */
  public weaponLockTimer: number = 0;
  static readonly WEAPON_LOCK_MIN = 0.3;

  constructor(
    scene: Phaser.Scene,
    waypoints: { x: number; y: number }[],
    enemyType: EnemyType = EnemyType.GRUNT,
    waveScaling: number = 0,
    grade: EnemyGrade = EnemyGrade.NORMAL,
    preview: boolean = false,
  ) {
    this.id = `e${Enemy.nextId++}`;
    this.scene = scene;
    this.isPreview = preview;
    this.waypoints = waypoints;
    this.enemyType = enemyType;
    this.grade = grade;
    this.waveScaling = waveScaling;

    const base = ENEMY_STATS[enemyType];
    const g = GRADE_MODIFIERS[grade];

    // Per-type stat overrides from balance chart (on top of global GOD mults)
    const typeOv = getEnemyTypeStatOverride(enemyType);
    this.typeRangeMult = typeOv.rangeMult;

    // HP = fixed base × grade multiplier (no wave/tier scaling — difficulty comes from spawn count)
    this.baseMaxHp = Math.round(base.hp * g.hpMult);
    this.maxHp = Math.round(this.baseMaxHp * GOD.enemyHpMult * typeOv.hpMult);
    this.hp = this.maxHp;
    // Speed in px/s: base tiles/s * TILE_SIZE × grade mult (no wave scaling)
    this.baseSpeed = base.speed * TILE_SIZE * g.speedMult;
    this.speed = this.baseSpeed * GOD.enemySpeedMult * DIFFICULTY_SPEED_MULT * typeOv.speedMult;
    this.reward = Math.round(base.reward * g.rewardMult);
    this.baseBaseDamage = Math.round(base.baseDamage * g.damageMult);
    this.baseDamage = Math.round(this.baseBaseDamage * GOD.enemyDamageMult * typeOv.damageMult * typeOv.meleeDamageMult);
    this.meleeCooldownDuration = base.meleeCooldown;
    this.mass = base.mass;

    // Multi-weapon setup (damage scaled by grade + per-type override)
    this.weaponDefs = base.weapons;
    this.weaponTimers = base.weapons.map(w => Math.random() * w.cooldown * GOD.weaponCooldownStagger);
    this.weaponBaseDamages = base.weapons.map(w => Math.round(w.damage * g.damageMult));
    this.weaponDamages = this.weaponBaseDamages.map(d => Math.round(d * GOD.enemyDamageMult * typeOv.damageMult * typeOv.weaponDamageMult));
    this.burstRemaining = base.weapons.map(() => 0);
    this.burstDelayTimer = base.weapons.map(() => 0);

    // Cloak setup
    this.cloakDuration = base.cloakDuration;
    this.cloakCooldownMax = base.cloakCooldown;
    // Start with a random partial cooldown so not all cloak at once
    this.cloakCooldown = base.cloakDuration > 0 ? Math.random() * base.cloakCooldown * 0.5 : 0;

    const start = waypoints[0];

    // Body sprite from enemy texture (Sprite supports both static and animated)
    const textureKey = ENEMY_TEXTURES[enemyType];
    const spriteInfo = ENEMY_SPRITE_FILES[enemyType];
    const walkKey = `enemy_${spriteInfo.file}_walk`;
    this.animKeyBase = `enemy_${spriteInfo.file}`;
    if (GOD.enemyAnimations && scene.textures.exists(walkKey)) {
      this.body = scene.add.sprite(start.x, start.y, walkKey, 0);
      this.hasWalkAnim = scene.anims.exists(walkKey);
      this.hasAttackAnim = !!spriteInfo.attack && scene.anims.exists(`enemy_${spriteInfo.attack}_attack`);
      this.hasDeathAnim = !!spriteInfo.death && scene.anims.exists(`enemy_${spriteInfo.death}_death`);
    } else {
      this.body = scene.add.sprite(start.x, start.y, textureKey);
    }
    // Scale sprite to match enemy radius, tunable via GOD.enemyScaleMult + per-type override
    const displaySize = Math.max(Enemy.MIN_DISPLAY_SIZE, base.radius * Enemy.SCALE_ENEMY) * GOD.enemyScaleMult * getEnemyTypeScale(enemyType);
    const texW = this.body.width;
    const texH = this.body.height;
    const maxDim = Math.max(texW, texH);
    const spriteScale = displaySize / maxDim;
    this.body.setScale(spriteScale);
    this.body.setOrigin(0.5, 0.5);
    this.body.setDepth(10);

    // Store base scale for Hive crawling animation
    if (enemyType === EnemyType.HIVE) {
      this.baseScaleX = spriteScale;
      this.baseScaleY = spriteScale;
      this.crawlPhase = Math.random() * Math.PI * 2; // random start phase
    }

    // Healer: green healing aura circle (always visible, gentle pulse in update)
    if (enemyType === EnemyType.HEALER && !preview) {
      this.healAura = scene.add.circle(start.x, start.y, GOD.healerRange, 0x44ff88, 0.1);
      this.healAura.setDepth(5); // below enemies
      this.healAura.setStrokeStyle(1.5, 0x44ff88, 0.3);
    }

    // Apply grade tint to sprite
    if (g.tint !== 0) {
      this.body.setTint(g.tint);
    }

    // All flyers get elevated depth, scale boost, and ground shadow
    if (FLYING_ENEMY_TYPES.has(enemyType)) {
      this.body.setDepth(15);
      this.flyBaseScale = spriteScale * Enemy.FLY_SCALE_BOOST;
      this.body.setScale(this.flyBaseScale);
      if (!preview) {
        const shadowW = displaySize * 0.9;
        const shadowH = displaySize * 0.3;
        this.flyShadow = scene.add.ellipse(
          start.x + Enemy.SHADOW_OFFSET_X,
          start.y + Enemy.SHADOW_OFFSET_Y,
          shadowW, shadowH, 0x000000, 0.22,
        ).setDepth(4);
      }
    } else if (!preview) {
      // Ground enemies: small tight shadow directly under body
      const shadowW = displaySize * 0.6;
      const shadowH = displaySize * 0.18;
      this.groundShadow = scene.add.ellipse(
        start.x, start.y + displaySize * 0.35,
        shadowW, shadowH, 0x000000, 0.12,
      ).setDepth(4);
    }

    // Visual radius = half the displayed sprite size, used for bar offsets
    // Includes previewScale so bars align with the actual rendered size
    this.visualRadius = (displaySize / 2) * this.previewScale;

    // Hologram overlay for cloak effect (blue-tinted rectangle, hidden by default)
    if (base.cloakDuration > 0) {
      this.cloakOverlay = scene.add.rectangle(start.x, start.y, displaySize, displaySize, 0x4488ff, 0)
        .setDepth(this.body.depth + 1).setBlendMode(Phaser.BlendModes.ADD);
    }

    // Sprite stacking: create layered ghost copies for Phantom/Specter
    if (!preview && (enemyType === EnemyType.PHANTOM || enemyType === EnemyType.SPECTER)) {
      this.hasSpriteStack = true;
      this.prevBodyX = start.x;
      this.prevBodyY = start.y;
      for (let i = 0; i < Enemy.STACK_LAYER_COUNT; i++) {
        const layer = scene.add.image(start.x, start.y, textureKey)
          .setScale(this.body.scaleX)
          .setOrigin(0.5, 0.5)
          .setDepth(this.body.depth - 1 - i) // below main sprite
          .setAlpha(0) // hidden when not moving
          .setTint(enemyType === EnemyType.SPECTER ? 0x4477cc : 0x3366aa)
          .setBlendMode(Phaser.BlendModes.ADD);
        this.spriteStackLayers.push(layer);
      }
    }

    // HP bar background
    const esm = GOD.enemyBarSizeMult;
    const barW = Math.max(8, base.radius * 1.2) * esm;
    const barH = 2 * esm;
    const barOffY = (this.visualRadius + Enemy.BAR_GAP) * GOD.enemyBarOffsetMult;
    this.hpBarBg = scene.add.rectangle(start.x, start.y - barOffY, barW, barH, 0x333333);
    this.hpBarBg.setDepth(11);

    // HP bar foreground
    this.hpBar = scene.add.rectangle(start.x, start.y - barOffY, barW, barH, base.color);
    this.hpBar.setDepth(12);

    // Hidden label (kept for grade badge reference, not displayed over sprite)
    this.label = scene.add.text(0, 0, '', {
      fontSize: '1px', color: '#000000',
    }).setVisible(false);

    // Collision radius from stats
    this.collisionRadius = base.radius;

    // Aggro setup: Grunts and Swarm rush toward nearby player robots
    this.canAggro = AGGRO_ENEMY_TYPES.has(enemyType);

    // Flyer setup: generate a realistic flight path with random waypoints across full map
    if (FLYING_ENEMY_TYPES.has(enemyType)) {
      this.isFlyer = true;
      if (!this.isPreview) {
        this.flyerPath = generateFlightPath(start, waypoints[waypoints.length - 1]);
        this.flyerWpIndex = 0;
      }
    } else if (!this.isPreview) {
      // Ground enemy: use A* pathfinding from spawn to base
      _recalcPath(this);
    }

    // Start moving toward first real waypoint
    this.currentWaypointIndex = 1;

    if (!this.isPreview) {
      EventBus.emit(GameEvents.ENEMY_SPAWNED, this);
    }
  }

  update(delta: number): void {
    if (!this.alive) return;

    const dt = delta / 1000;

    // ── Terrain dying: HP drain + visual, skip all normal behavior ──
    if (this.terrainDyingType) {
      _updateTerrainDying(this, dt);
      return;
    }

    // Melee cooldown tick
    if (!this.isPreview && this.meleeCooldown > 0) this.meleeCooldown -= dt;
    if (!this.isPreview && this.weaponLockTimer > 0) this.weaponLockTimer -= dt;

    // Combo decay
    if (this.comboTimer > 0) {
      this.comboTimer -= dt;
      if (this.comboTimer <= 0) { this.comboTypes.clear(); this.comboTimer = 0; }
    }

    // Burn DOT tick
    if (!this.isPreview) _updateBurn(this, dt);

    // Hive crawling animation — horizontal scaleX oscillation
    if (this.enemyType === EnemyType.HIVE && !this.isPreview) {
      this.crawlPhase = (this.crawlPhase ?? 0) + dt * 5;
      const distort = 1 + 0.08 * Math.sin(this.crawlPhase);
      this.body.setScale(
        this.body.scaleX > 0 ? Math.abs(this.baseScaleX ?? this.body.scaleX) * distort : -Math.abs(this.baseScaleX ?? this.body.scaleX) * distort,
        (this.baseScaleY ?? this.body.scaleY) * (1 - 0.04 * Math.sin(this.crawlPhase)),
      );
    }

    // Healer aura: keep visual circle following the healer + gentle pulse
    if (this.enemyType === EnemyType.HEALER && this.healAura && !this.isPreview) {
      this.healAura.setPosition(this.body.x, this.body.y);
      const pulse = 0.12 + 0.06 * Math.sin(Date.now() * 0.004);
      this.healAura.setAlpha(pulse);
    }

    // Dying animation: skip all logic, wait for anim to complete
    if (this.isDying) return;

    // Stun: skip movement while stunned
    if (this.stunTimer > 0) {
      this.playAnim('idle');
      this.stunTimer = Math.max(0, this.stunTimer - dt);
      // Visual feedback: gentle alpha pulse (skip on last frame)
      if (this.stunTimer > 0) {
        this.body.setAlpha(0.7 + 0.3 * Math.sin(this.stunTimer * 8));
      }

      // Spinning stars just below the HP bar
      if (!this.stunStars) {
        this.stunStars = this.scene.add.container(this.body.x, this.body.y).setDepth(this.body.depth + 2);
        for (const ch of ['★', '✦', '★']) {
          this.stunStars.add(this.scene.add.text(0, 0, ch, {
            fontSize: '16px', color: '#ffdd44', fontFamily: 'monospace',
            stroke: '#000000', strokeThickness: 3,
          }).setOrigin(0.5));
        }
      }
      // Position just below HP bar
      const barY = this.body.y - (this.visualRadius + Enemy.BAR_GAP) * GOD.enemyBarOffsetMult;
      const starY = barY + 6;
      this.stunStars.setPosition(this.body.x, starY);
      this.stunStarsAngle += dt * 3;
      const starRadius = 16;
      const children = this.stunStars.getAll() as Phaser.GameObjects.Text[];
      for (let i = 0; i < children.length; i++) {
        const a = this.stunStarsAngle + (Math.PI * 2 * i) / children.length;
        children[i].setPosition(Math.cos(a) * starRadius, Math.sin(a) * starRadius * 0.4);
        children[i].setAlpha(0.7 + 0.3 * Math.sin(a * 2));
      }

      if (this.stunTimer <= 0) {
        this.stunStars?.destroy();
        this.stunStars = null;
        this.body.setAlpha(this.isPreview ? 1 : this.fogVisibility);
      }
    } else if (this.isKnockedBack) {
      // Billiard knockback: skip normal movement, BilliardKnockback system handles position
    } else {
      // Cloak logic
      _updateCloak(this, dt);

      // Dash timer tick
      if (this.dashCooldown > 0) this.dashCooldown -= dt;
      if (this.dashTimer > 0) {
        this.dashTimer -= dt;
        if (this.dashTimer <= 0) this.isDashing = false;
      }

      // Runner dash trigger: when near aggro target and off cooldown
      if (this.enemyType === EnemyType.RUNNER && !this.isDashing && this.dashCooldown <= 0 && this.aggroTarget) {
        const ddx = this.aggroTarget.x - this.body.x;
        const ddy = this.aggroTarget.y - this.body.y;
        if (ddx * ddx + ddy * ddy < Enemy.DASH_TRIGGER_RANGE * Enemy.DASH_TRIGGER_RANGE) {
          this.isDashing = true;
          this.dashTimer = Enemy.DASH_DURATION;
          this.dashCooldown = Enemy.DASH_COOLDOWN;
          SoundManager.play('runnerGrowl');
          // Visual flash
          this.body.setTint(0xffff00);
          this.scene.time.delayedCall(150, () => { if (this.alive) this.body.clearTint(); });
        }
      }

      // Movement (skip for preview entities)
      if (!this.isPreview) {
        const prevX = this.body.x;
        const prevY = this.body.y;
        if (this.isFlyer) {
          _updateFlyer(this, dt);
        } else {
          _updateGround(this, dt);
        }
        // Post-movement: clamp to map bounds, block walls for ground units
        _clampToValidPosition(this, prevX, prevY);

        // Animation: always walk while alive and not attacking
        if (this.animState !== 'attack') {
          this.playAnim('walk');
        }
      }
    }

    // Face toward current target (firing > aggro > waypoint direction)
    // Deadzone + cooldown prevent rapid flip oscillation
    const faceTarget = this.firingTarget ?? this.aggroTarget;
    if (faceTarget) {
      const fdx = faceTarget.x - this.body.x;
      const deadzone = this.firingTarget ? 8 : GOD.flipDeadzone;
      if (Math.abs(fdx) > deadzone) {
        const wantFlip = fdx < 0;
        if (wantFlip !== this.body.flipX) {
          const now = this.scene.time.now;
          if (now - this.lastFlipTime >= GOD.flipCooldownMs) {
            this.body.setFlipX(wantFlip);
            this.lastFlipTime = now;
          }
        }
      }
    }

    // Update HP bar + label position (offset by visual radius, scaled by GOD)
    const barY = this.body.y - (this.visualRadius + Enemy.BAR_GAP) * GOD.enemyBarOffsetMult;
    this.hpBarBg.setPosition(this.body.x, barY);
    this.hpBar.setPosition(this.body.x, barY);
    this.hpBar.setScale(this.hp / this.maxHp, 1);
    this.label.setPosition(this.body.x, barY - 5);

    // Shadows follow body — flyers bob in altitude
    if (this.flyShadow) {
      const t = this.scene.time.now / 1000;
      const wave = Math.sin(t * GOD.flyAltitudeSpeed + this.flyAltitudePhase);
      const altExtra = wave * GOD.flyAltitudeShadowAmp;
      this.flyShadow.setPosition(
        this.body.x + Enemy.SHADOW_OFFSET_X + altExtra * 0.5,
        this.body.y + Enemy.SHADOW_OFFSET_Y + altExtra,
      );
      this.flyShadow.setAlpha(this.fogVisible ? (0.22 - wave * 0.06) : 0);
      // Scale oscillation: altitude bobbing
      this.body.setScale(this.flyBaseScale * (1 + wave * GOD.flyAltitudeScaleAmp));
    }
    if (this.groundShadow) {
      this.groundShadow.setPosition(this.body.x, this.body.y + this.visualRadius * 0.7);
      this.groundShadow.setAlpha(this.fogVisible ? 0.12 : 0);
    }

    // Sprite stacking: update layered ghost copies for Phantom/Specter
    if (this.hasSpriteStack) {
      _updateSpriteStack(this, dt);
    }

    // Gold grade: periodic sparkle particles
    if (this.grade === EnemyGrade.GOLD && this.fogVisible) {
      this.goldSparkleTimer += dt;
      if (this.goldSparkleTimer >= 0.15) {
        this.goldSparkleTimer = 0;
        const sx = this.body.x + (Math.random() - 0.5) * this.visualRadius * 1.5;
        const sy = this.body.y + (Math.random() - 0.5) * this.visualRadius * 1.5;
        const spark = this.scene.add.circle(sx, sy, 1.5 + Math.random(), 0xffdd44, 0.8)
          .setDepth(this.body.depth + 1);
        this.scene.tweens.add({
          targets: spark,
          alpha: 0, scaleX: 0.2, scaleY: 0.2,
          y: sy - 8 - Math.random() * 6,
          duration: 300 + Math.random() * 200,
          onComplete: () => spark.destroy(),
        });
      }
    }
  }

  /**
   * Update ranged shooting: finds closest robot in range, fires projectile.
   * Called each frame. Only one weapon can fire at a time (global lock shared with CQC).
   */
  updateShooting(
    dt: number,
    robots: RobotTarget[],
    baseTarget?: { x: number; y: number },
  ): void {
    _updateShooting(this, dt, robots, baseTarget);
  }

  /**
   * Feed nearby robot positions so aggro enemies can rush them.
   * Called each frame from RobotManager before enemy.update().
   */
  updateAggroTarget(robotPositions: { x: number; y: number }[], robots?: Robot[], dt: number = 1 / 60): void {
    _updateAggroTarget(this, robotPositions, robots, dt);
  }

  /**
   * Pre-compute firingTarget before WaveManager.computeSeparation().
   * This ensures arc formation steering uses the correct target this frame.
   * Called from WaveManager BEFORE computeSeparation() and enemy.update().
   */
  precomputeFiringTarget(): void {
    if (!this.alive || this.weaponDefs.length === 0 || this.camoConfusionTimer > 0) {
      this.firingTarget = null;
      return;
    }
    const maxRange = Math.max(...this.weaponDefs.map(w => w.range * GOD.enemyWeaponRangeMult * this.typeRangeMult));
    // Hysteresis: if already firing, allow 10% extra range before disengaging
    const effectiveRange = this.firingTarget ? maxRange * 1.10 : maxRange;

    const checkLOS = !this.isFlyer; // flyers ignore walls

    // Check aggro target
    if (this.aggroTarget) {
      const dx = this.aggroTarget.x - this.body.x;
      const dy = this.aggroTarget.y - this.body.y;
      if (Math.sqrt(dx * dx + dy * dy) <= effectiveRange) {
        // Ground enemies can't fire through walls
        if (!checkLOS || hasLineOfSight(this.body.x, this.body.y, this.aggroTarget.x, this.aggroTarget.y)) {
          this.firingTarget = this.aggroTarget;
          return;
        }
      }
    }

    // Check nearby robots within weapon range (non-aggro weapon engagement)
    if (this.nearbyRobots.length > 0) {
      let best: { x: number; y: number } | null = null;
      let bestDist = effectiveRange;
      for (const r of this.nearbyRobots) {
        if (!r.alive || r.isCamoActive) continue;
        const dx = r.x - this.body.x;
        const dy = r.y - this.body.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < bestDist) {
          // Ground enemies can't fire through walls
          if (checkLOS && !hasLineOfSight(this.body.x, this.body.y, r.x, r.y)) continue;
          bestDist = d; best = { x: r.x, y: r.y };
        }
      }
      if (best) { this.firingTarget = best; return; }
    }

    // Check base proximity
    const baseWp = this.waypoints[this.waypoints.length - 1];
    if (baseWp) {
      const bx = baseWp.x - this.body.x;
      const by = baseWp.y - this.body.y;
      if (Math.sqrt(bx * bx + by * by) <= effectiveRange) {
        this.firingTarget = baseWp;
        return;
      }
    }

    this.firingTarget = null;
    this.notFiringTimer = 0; // no target → reset close-in pressure
  }

  /** How far along the path this enemy is (0 = spawn, 1 = base). */
  getPathProgress(): number {
    const total = this.waypoints.length;
    if (total <= 1) return 0;
    return Math.min(1, this.currentWaypointIndex / (total - 1));
  }

  /** After losing aggro target, recalculate A* path from current position. */
  resyncToNearestWaypoint(): void {
    _resyncToNearestWaypoint(this);
  }

  /** Mark path as needing recalculation (called after collision push). */
  markPathDirty(): void {
    if (this.isFlyer) return;
    this.pathDirty = true;
  }

  /** Set fog-of-war visibility factor (0 = hidden, 1 = fully visible). */
  setFogVisibility(factor: number): void {
    this.fogVisibility = factor;
    const visible = factor > 0.01;

    if (visible !== this.fogVisible) {
      this.fogVisible = visible;
      if (!visible) {
        this.body.setVisible(false);
        this.hpBar.setVisible(false);
        this.hpBarBg.setVisible(false);
        this.label.setVisible(false);
        return;
      } else {
        this.body.setVisible(true);
        if (!this.isCloaked) {
          this.hpBar.setVisible(true);
          this.hpBarBg.setVisible(true);
          this.label.setVisible(true);
        }
      }
    }

    // Apply gradual alpha (if not cloaked — cloak manages its own alpha)
    if (visible && !this.isCloaked) {
      this.body.setAlpha(factor);
      this.hpBar.setAlpha(factor);
      this.hpBarBg.setAlpha(factor);
      this.label.setAlpha(factor);
    }
  }

  /**
   * Try a melee lunge toward a target position (called by collision system).
   * Returns true if attack was triggered (cooldown ready).
   */
  tryMeleeLunge(targetX: number, targetY: number): boolean {
    if (!this.alive || this.isLunging || this.meleeCooldown > 0 || this.weaponLockTimer > 0) return false;
    this.isLunging = true;
    this.meleeCooldown = this.meleeCooldownDuration;
    this.weaponLockTimer = Math.max(Enemy.WEAPON_LOCK_MIN, this.meleeCooldownDuration * 0.5);
    this.playAnim('attack');
    this.body.once('animationcomplete', () => {
      if (this.alive && this.animState === 'attack') this.playAnim('walk');
    });

    const dx = targetX - this.body.x;
    const dy = targetY - this.body.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 0.1) { this.isLunging = false; return false; }

    const nx = dx / dist;
    const ny = dy / dist;
    const lungeX = this.body.x + nx * Enemy.MELEE_LUNGE_DIST;
    const lungeY = this.body.y + ny * Enemy.MELEE_LUNGE_DIST;
    const origX = this.body.x;
    const origY = this.body.y;

    // Quick lunge forward then snap back
    this.scene.tweens.add({
      targets: this.body,
      x: lungeX,
      y: lungeY,
      duration: 80,
      ease: 'Power2',
      yoyo: true,
      onComplete: () => {
        // Restore position (in case movement happened during tween)
        if (this.alive) {
          this.body.setPosition(origX, origY);
        }
        this.isLunging = false;
      },
    });
    return true;
  }

  /** Play an animation state (walk/attack/death/idle). No-op if animations disabled or same state. */
  playAnim(state: 'idle' | 'walk' | 'attack' | 'death'): void {
    if (!GOD.enemyAnimations || this.animState === state) return;
    if (state === 'attack' && !this.hasAttackAnim) return;
    if (state === 'death' && !this.hasDeathAnim) return;
    if (state === 'walk' && !this.hasWalkAnim) return;

    this.animState = state;
    if (state === 'idle') {
      this.body.stop();
      if (this.hasWalkAnim) this.body.setFrame(0);
    } else {
      const key = `${this.animKeyBase}_${state}`;
      if (this.scene.anims.exists(key)) {
        this.body.play(key);
      }
    }
  }

  /** Stun the enemy for a duration (seconds). Stuns stack by taking the max. */
  stun(duration: number): void {
    if (!this.alive) return;
    const wasStunned = this.stunTimer > 0;
    this.stunTimer = Math.max(this.stunTimer, duration);
    if (!wasStunned && this.stunTimer > 0) {
      EventBus.emit(GameEvents.ENEMY_STUNNED);
    }
  }

  /**
   * Push ground enemy back along its path.
   * @param maxTiles - maximum tiles to push (capped at 3)
   * @param proximity - 0..1 factor (1 = at explosion center, 0 = at edge). Scales distance.
   * Flyers are unaffected.
   */
  knockBack(maxTiles: number, proximity: number = 1): void {
    if (!this.alive || this.isFlyer) return;

    // Billiard mode: velocity-based knockback with bounces
    if (GOD.billiardEnabled && BilliardKnockback.instance) {
      // Heavy enemies still get pushed (mass reduces velocity naturally)
      const cappedTiles = Math.min(maxTiles, GOD.jumperMaxKnockbackTiles);
      const massFactor = Math.max(0.2, 50 / this.mass);
      const maxDist = cappedTiles * proximity * TILE_SIZE * Math.min(1, massFactor);
      if (maxDist <= 0) return;

      // Direction: away from next waypoint (backward along path)
      const wp = this.waypoints[this.currentWaypointIndex];
      if (!wp) return;
      const dx = this.body.x - wp.x;
      const dy = this.body.y - wp.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 0.1) return;

      // Convert distance to velocity
      const speed = maxDist * GOD.billiardSpeedMult;
      const vx = (dx / dist) * speed;
      const vy = (dy / dist) * speed;
      BilliardKnockback.instance.applyKnockback(this, vx, vy, 'explosion');
      return;
    }

    // Legacy instant knockback (billiardEnabled = false)
    if (this.mass >= KNOCKBACK_IMMUNE_MASS) return;

    const cappedTiles = Math.min(maxTiles, GOD.jumperMaxKnockbackTiles);
    const massFactor = Math.max(0.2, 50 / this.mass);
    const maxDist = cappedTiles * proximity * TILE_SIZE * Math.min(1, massFactor);
    if (maxDist <= 0) return;

    let remaining = maxDist;
    let idx = this.currentWaypointIndex;
    let cx = this.body.x;
    let cy = this.body.y;

    while (idx > 1 && remaining > 0) {
      const prev = this.waypoints[idx - 1];
      if (!prev) break;
      const dx = prev.x - cx;
      const dy = prev.y - cy;
      const segDist = Math.sqrt(dx * dx + dy * dy);
      if (segDist <= remaining) {
        remaining -= segDist;
        cx = prev.x;
        cy = prev.y;
        idx--;
      } else {
        const ratio = remaining / segDist;
        cx += dx * ratio;
        cy += dy * ratio;
        remaining = 0;
      }
    }

    this.currentWaypointIndex = idx;
    this.body.setPosition(cx, cy);
    _recalcPath(this);
  }

  /**
   * Push enemy in a specific world direction (not along path).
   * Used by side-mounted Jumper for directional terrain control.
   */
  pushInDirection(dirX: number, dirY: number, tiles: number): { x: number; y: number } | null {
    if (!this.alive || this.isFlyer) return null;

    // Billiard mode: velocity-based push with bounces
    if (GOD.billiardEnabled && BilliardKnockback.instance) {
      const massFactor = Math.max(0.2, 50 / this.mass);
      const effectiveTiles = Math.min(tiles, GOD.jumperMaxKnockbackTiles) * Math.min(1, massFactor);
      const speed = effectiveTiles * TILE_SIZE * GOD.billiardSpeedMult;
      BilliardKnockback.instance.applyKnockback(this, dirX * speed, dirY * speed, 'jumper');
      // Return estimated final position for Jumper animation target
      return { x: this.body.x + dirX * effectiveTiles * TILE_SIZE, y: this.body.y + dirY * effectiveTiles * TILE_SIZE };
    }

    // Legacy instant push (billiardEnabled = false)
    if (this.mass >= KNOCKBACK_IMMUNE_MASS) return null;

    const massFactor = Math.max(0.2, 50 / this.mass);
    const effectiveTiles = Math.min(tiles, GOD.jumperMaxKnockbackTiles) * Math.min(1, massFactor);

    let cx = this.body.x;
    let cy = this.body.y;
    const stepX = dirX * TILE_SIZE;
    const stepY = dirY * TILE_SIZE;
    const steps = Math.round(effectiveTiles);

    for (let i = 0; i < steps; i++) {
      const nx = cx + stepX;
      const ny = cy + stepY;
      const col = Math.floor(nx / TILE_SIZE);
      const row = Math.floor(ny / TILE_SIZE);

      if (row < 0 || row >= MAP_GRID.length || col < 0 || col >= (MAP_GRID[0]?.length ?? 0)) break;

      const tile = MAP_GRID[row][col];

      if (tile === TileType.WATER || tile === TileType.MAGMA) {
        cx = nx; cy = ny;
        break;
      }

      const decor = getDecorAt(col, row);
      if (decor && DECOR_INTERACTIONS[decor.type].interaction === DecorInteraction.KILL) {
        cx = nx; cy = ny;
        break;
      }

      if (tile === TileType.WALL) break;
      if (decor && DECOR_INTERACTIONS[decor.type].interaction === DecorInteraction.BLOCK) break;

      cx = nx;
      cy = ny;
    }

    this.body.setPosition(cx, cy);
    _recalcPath(this);

    const landCol = Math.floor(cx / TILE_SIZE);
    const landRow = Math.floor(cy / TILE_SIZE);
    if (landRow >= 0 && landRow < MAP_GRID.length && landCol >= 0 && landCol < (MAP_GRID[0]?.length ?? 0)) {
      const landTile = MAP_GRID[landRow][landCol];
      if (landTile === TileType.WATER) {
        _startTerrainDeath(this, 'water');
      } else if (landTile === TileType.MAGMA) {
        _startTerrainDeath(this, 'magma');
      } else {
        const landDecor = getDecorAt(landCol, landRow);
        if (landDecor && DECOR_INTERACTIONS[landDecor.type].interaction === DecorInteraction.KILL) {
          _startTerrainDeath(this, 'chasm');
        }
      }
    }

    return { x: cx, y: cy };
  }

  /** Current velocity vector (px/s) based on movement toward next waypoint. */
  getVelocity(): { vx: number; vy: number } {
    let tx: number, ty: number;
    if (this.isFlyer) {
      const wp = this.flyerPath[this.flyerWpIndex];
      if (!wp) return { vx: 0, vy: 0 };
      tx = wp.x; ty = wp.y;
    } else {
      const wp = this.waypoints[this.currentWaypointIndex];
      if (!wp) return { vx: 0, vy: 0 };
      tx = wp.x; ty = wp.y;
    }
    const dx = tx - this.body.x;
    const dy = ty - this.body.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 0.1) return { vx: 0, vy: 0 };
    const s = this.speed;
    return { vx: (dx / dist) * s, vy: (dy / dist) * s };
  }

  takeDamage(amount: number, projType?: ProjectileType, piercing?: boolean, isCrit?: boolean, fromCamo?: boolean): void {
    if (!this.alive) return;
    if (GOD.oneHitKill) {
      this.hp = 0;
      if (this.enemyType === EnemyType.HIVE) this.emitHiveDeathSpawn();
      this.startDeath();
      return;
    }

    // Hit by invisible attacker → confusion panic
    if (fromCamo && this.camoConfusionTimer <= 0) {
      this.camoConfusionTimer = Enemy.CAMO_CONFUSION_DURATION;
      _showConfusionPopup(this);
    }
    let mult = projType ? getResistanceMultiplier(this.enemyType, projType) : 1.0;
    // Piercing: halve resistance penalty (bring mult halfway toward 1.0)
    if (piercing && mult < 1.0) {
      mult = (mult + 1.0) / 2;
    }

    // ── Weapon combo: track distinct projectile types hitting this enemy ──
    if (projType) {
      const hadTypes = this.comboTypes.size;
      this.comboTypes.add(projType);
      this.comboTimer = Enemy.COMBO_WINDOW;
      // Apply combo multiplier (only when 2+ distinct types)
      const comboLevel = Math.min(this.comboTypes.size, Enemy.COMBO_MULTS.length - 1);
      const comboMult = Enemy.COMBO_MULTS[comboLevel];
      mult *= comboMult;
      // Show combo popup when a new type is added (2+)
      if (this.comboTypes.size > hadTypes && this.comboTypes.size >= 2) {
        _spawnComboPopup(this, this.comboTypes.size, comboMult);
      }
    }

    const finalDmg = amount * mult;
    this.hp -= finalDmg;
    // Track biggest single hit for achievements
    EventBus.emit(GameEvents.ENEMY_DAMAGE_DEALT, { damage: finalDmg });

    // ── JRPG-style floating damage number ──
    this.spawnDamageNumber(finalDmg, mult, isCrit);

    // Hit spark — size proportional to damage
    _spawnHitSpark(this, finalDmg);

    // Hit flash feedback — brief white tint, then restore grade tint
    this.body.setTint(0xffffff);
    this.scene.time.delayedCall(60, () => {
      if (!this.alive) return;
      const gradeTint = GRADE_MODIFIERS[this.grade].tint;
      if (gradeTint !== 0) {
        this.body.setTint(gradeTint);
      } else {
        this.body.clearTint();
      }
    });

    if (this.hp <= 0) {
      this.hp = 0;
      if (this.enemyType === EnemyType.HIVE) this.emitHiveDeathSpawn();
      this.startDeath();
    }
  }

  /** Emit event to spawn Wasps at Hive death position */
  private emitHiveDeathSpawn(): void {
    const min = GOD.hiveWaspMinCount;
    const max = GOD.hiveWaspMaxCount;
    const count = min + Math.floor(Math.random() * (max - min + 1));
    EventBus.emit(GameEvents.HIVE_DEATH_SPAWN, {
      x: this.body.x,
      y: this.body.y,
      count,
      grade: this.grade,
      waveScaling: this.waveScaling,
    });
  }

  /** Set this enemy on fire — DOT damage + fire/smoke particles + sizzle sound.
   *  Refreshes duration if already burning, keeps highest DPS. */
  applyBurn(duration: number, dps: number): void {
    if (!this.alive) return;
    this.burnTimer = Math.max(this.burnTimer, duration);
    this.burnDps = Math.max(this.burnDps, dps);
  }

  /** @internal Spawn a floating damage number (called from EnemyVisuals.updateBurn too). */
  spawnDamageNumber(dmg: number, resistMult: number, isCrit = false): void {
    const displayDmg = Math.round(dmg);
    if (displayDmg <= 0) return;

    // Base font size from damage magnitude
    const baseFontSize = popupFontSize(displayDmg);

    // Color & size modifier based on resistance effectiveness
    let color = '#ffffff';   // normal
    let sizeMult = 1.0;
    let prefix = '';

    if (isCrit) {
      // Critical hit: gold, larger, with "!" suffix
      color = '#ffcc00';
      sizeMult = 1.4;
      prefix = '';
    } else if (resistMult >= 1.5) {
      color = '#ff4444';
      sizeMult = 1.3;
      prefix = '';
    } else if (resistMult > 1.0) {
      color = '#ffdd44';
      sizeMult = 1.15;
    } else if (resistMult < 0.5) {
      color = '#888888';
      sizeMult = 0.8;
    } else if (resistMult < 1.0) {
      color = '#aaaaaa';
      sizeMult = 0.9;
    }
    const fontSize = Math.round(baseFontSize * sizeMult);

    // Random horizontal offset for chaos effect (scaled by GOD.popupDistMult)
    const pDist = GOD.popupDistMult;
    const spreadX = (Math.random() - 0.5) * 30 * pDist;
    const startX = this.body.x + spreadX;
    const startY = this.body.y - (this.visualRadius + 5) * GOD.popupStartOffsetMult;

    const suffix = isCrit ? '!' : '';
    const txt = VFXPool.ready
      ? VFXPool.acquireText(startX, startY, prefix + displayDmg + suffix, {
          fontFamily: 'monospace',
          fontSize: `${fontSize}px`,
          fontStyle: 'bold',
          color,
          stroke: '#000000',
          strokeThickness: 3,
        })
      : this.scene.add.text(startX, startY, prefix + displayDmg + suffix, {
          fontFamily: 'monospace',
          fontSize: `${fontSize}px`,
          fontStyle: 'bold',
          color,
          stroke: '#000000',
          strokeThickness: 3,
        });
    txt.setOrigin(0.5, 1);
    txt.setDepth(50);

    // Animate: float upward with slight random drift, scale pop, then fade
    const driftX = (Math.random() - 0.5) * 20 * pDist;
    const pDur = GOD.popupDurationMult;
    const usePool = VFXPool.ready;
    this.scene.tweens.add({
      targets: txt,
      y: startY - (25 + Math.random() * 15) * pDist,
      x: startX + driftX,
      alpha: 0,
      scale: isCrit ? 1.4 : resistMult >= 1.5 ? 1.3 : 1.0,
      duration: (600 + Math.random() * 200) * pDur,
      ease: 'Power2',
      onComplete: () => usePool ? VFXPool.releaseText(txt) : txt.destroy(),
    });

    // Initial pop-in: quick scale up then settle
    if (isCrit || resistMult >= 1.5) {
      txt.setScale(0.5);
      this.scene.tweens.add({
        targets: txt,
        scale: 1.3,
        duration: 120,
        ease: 'Back.easeOut',
      });
    }
  }

  /** @internal Delegates to EnemyCombat.onReachedBase */
  onReachedBase(): void {
    _onReachedBase(this);
  }

  /** @internal Delegates to EnemyCombat.updateBaseSiege */
  updateBaseSiege(dt: number): void {
    _updateBaseSiege(this, dt);
  }

  /** @internal Delegates to EnemyVisuals.showAlertPopup */
  showAlertPopup(): void {
    _showAlertPopup(this);
  }

  /** @internal Delegates to EnemyVisuals.startTerrainDeath */
  startTerrainDeath(type: 'water' | 'magma' | 'chasm'): void {
    _startTerrainDeath(this, type);
  }

  /** Live-refresh visuals + stats when GOD params change (works even when paused) */
  refreshGodVisuals(): void {
    if (!this.alive) return;

    // Live-update GOD stat multipliers (HP, speed, damage) + per-type overrides
    const typeOv = getEnemyTypeStatOverride(this.enemyType);
    const newMaxHp = Math.round(this.baseMaxHp * GOD.enemyHpMult * typeOv.hpMult);
    if (newMaxHp !== this.maxHp) {
      const ratio = this.maxHp > 0 ? this.hp / this.maxHp : 1;
      this.maxHp = newMaxHp;
      this.hp = Math.round(newMaxHp * ratio);
    }
    this.speed = this.baseSpeed * GOD.enemySpeedMult * DIFFICULTY_SPEED_MULT * typeOv.speedMult;
    this.typeRangeMult = typeOv.rangeMult;
    this.baseDamage = Math.round(this.baseBaseDamage * GOD.enemyDamageMult * typeOv.damageMult * typeOv.meleeDamageMult);
    // Update weapon damages with GOD multiplier + per-type override
    for (let i = 0; i < this.weaponBaseDamages.length; i++) {
      this.weaponDamages[i] = Math.round(this.weaponBaseDamages[i] * GOD.enemyDamageMult * typeOv.damageMult * typeOv.weaponDamageMult);
    }

    // Recalculate sprite scale from GOD.enemyScaleMult + per-type override (preserve preview scale)
    const displaySize = Math.max(Enemy.MIN_DISPLAY_SIZE, this.collisionRadius * Enemy.SCALE_ENEMY) * GOD.enemyScaleMult * getEnemyTypeScale(this.enemyType);
    const maxDim = Math.max(this.body.width, this.body.height);
    const spriteScale = (displaySize / maxDim) * this.previewScale;
    this.body.setScale(spriteScale);
    this.visualRadius = (displaySize / 2) * this.previewScale;

    // Reposition + resize HP bar with updated offsets & size
    const barY = this.body.y - (this.visualRadius + Enemy.BAR_GAP) * GOD.enemyBarOffsetMult;
    const esm = GOD.enemyBarSizeMult;
    const newBarW = Math.max(8, this.collisionRadius * 1.2) * esm;
    const newBarH = 2 * esm;
    this.hpBarBg.setPosition(this.body.x, barY);
    this.hpBarBg.setSize(newBarW, newBarH);
    this.hpBar.setPosition(this.body.x, barY);
    this.hpBar.setSize(newBarW, newBarH);
    this.hpBar.setScale(this.hp / this.maxHp, 1);
    this.label.setPosition(this.body.x, barY - 5);
  }

  /** Handle enemy death: emit event, play death anim if available, then destroy. */
  private startDeath(): void {
    this.alive = false;
    EventBus.emit(GameEvents.ENEMY_DESTROYED, this);

    if (GOD.enemyAnimations && this.hasDeathAnim) {
      this.isDying = true;
      this.playAnim('death');
      this.hpBar.setVisible(false);
      this.hpBarBg.setVisible(false);
      this.label.setVisible(false);
      this.healAura?.setVisible(false);
      this.stunStars?.setVisible(false);
      this.body.once('animationcomplete', () => {
        if (this.isDying) { this.isDying = false; this.destroyGraphics(); }
      });
      // Safety timeout in case anim doesn't fire complete
      this.scene.time.delayedCall(1500, () => {
        if (this.isDying) { this.isDying = false; this.destroyGraphics(); }
      });
    } else {
      this.destroyGraphics();
    }
  }

  destroy(): void {
    this.alive = false;
    this.destroyGraphics();
  }

  private destroyGraphics(): void {
    this.body.destroy();
    this.flyShadow?.destroy();
    this.flyShadow = undefined;
    this.groundShadow?.destroy();
    this.groundShadow = undefined;
    this.hpBar.destroy();
    this.hpBarBg.destroy();
    this.label.destroy();
    this.cloakOverlay?.destroy();
    this.stunStars?.destroy();
    this.healAura?.destroy();
    this.healAura = null;
    for (const l of this.spriteStackLayers) l.destroy();
    this.spriteStackLayers.length = 0;
    for (const p of this.burnParticles) p.destroy();
    this.burnParticles.length = 0;
  }
}
